import { IVectorLayerGroupConfig } from "esdm-map/dist/layers/groups/vector/interfaces/IVectorLayerGroupConfig";
import { IWmsLayerGroupConfig } from "esdm-map/dist/layers/groups/wms/interfaces/IWmsLayerGroupConfig";
import { WfsParamSourceMapServer, IWfsParamSource, WfsBboxAsWktRequestParams } from "esdm-map/dist/layers/vector/sources/wfs";
import { VectorLayerStrategy } from "esdm-map/dist/layers/vector/VectorLayerStrategy";
import { LhlpRecordSubType, LhlpRecordType } from "../defs/LhlpRecordType";
import { LhlpMapConfig } from "../defs/LhlpMapConfig";
import { type } from "jquery";
import { ZoomToDataBounds } from "esdm-map/dist/layers/vector/ZoomToDataBounds";

export class DefaultLayerConfigurationFactory {

    private mapServerProxyUrl: string = "/api/MapServer/lhlp";
    private ogcProxyUrl: string = "/api/OgcService/NHLE";
    private siteId: string;
    private siteFolder: string;
    private mapConfig: LhlpMapConfig;
    
    private paramSource3857(): IWfsParamSource {
        return new WfsParamSourceMapServer(new WfsBboxAsWktRequestParams(), {
            srsName: "EPSG:3857"
        });        
    }

    constructor(siteid: string, sitefolder: string, mapconfig: LhlpMapConfig) {
        this.siteId = siteid;
        this.siteFolder = sitefolder;
        if(this.siteFolder!='') { // the URLs to the proxies must be on the tenant site for the claims to work properly
            this.mapServerProxyUrl = '/' + this.siteFolder + this.mapServerProxyUrl;
            this.ogcProxyUrl = '/' + this.siteFolder + this.ogcProxyUrl;
        }
        this.mapConfig = mapconfig;
    }

    public layerConfig = (
        recordType: LhlpRecordType,
        recordSubType: LhlpRecordSubType = null,
        opts: IVectorLayerGroupConfig = {},
        isPublic: boolean = false,
        assetId: number = 0
    ): IVectorLayerGroupConfig | IWmsLayerGroupConfig =>
    {
        let layerConfig: IVectorLayerGroupConfig = null;

        if (recordSubType !== null) {

            switch (recordSubType) {

                case LhlpRecordSubType.AssetTypeBuilding:
                    layerConfig = this.AssetTypeBuilding(opts, isPublic);
                    break;
    
                case LhlpRecordSubType.AssetTypeParkOrGarden:
                    layerConfig = this.AssetTypeParkOrGarden(opts, isPublic);
                    break;
    
                case LhlpRecordSubType.AssetTypeLandmarkOrArtwork:
                    layerConfig = this.AssetTypeLandmarkOrArtwork(opts, isPublic);
                    break;
    
                case LhlpRecordSubType.AssetTypeOtherSiteStructure:
                    layerConfig = this.AssetTypeOtherSiteStructure(opts, isPublic);
                    break; 

                case LhlpRecordSubType.ListedBuilding:
                    layerConfig = this.ListedBuilding(opts);
                    break;

                case LhlpRecordSubType.ScheduledMonument:
                    layerConfig = this.ScheduledMonument(opts);
                    break;    
                                
                case LhlpRecordSubType.RegisteredParkAndGarden:
                    layerConfig = this.RegisteredParkAndGarden(opts);
                    break; 

                case LhlpRecordSubType.Battlefield:
                    layerConfig = this.Battlefield(opts);
                    break; 

                case LhlpRecordSubType.ProtectedWreck:
                    layerConfig = this.ProtectedWreck(opts);
                    break; 

                case LhlpRecordSubType.WorldHeritageSite:
                    layerConfig = this.WorldHeritageSite(opts);
                    break;
            }
        }

        if(layerConfig === null) {
            
            switch (recordType) {  
                case LhlpRecordType.LocalListArea:
                    layerConfig = this.LocalListArea(opts);
                    break;

                case LhlpRecordType.LocalListSubAreas:
                    layerConfig = this.LocalListSubAreas(opts);
                    break;

                case LhlpRecordType.ASSET:
                    layerConfig = this.Asset(opts, recordSubType, assetId);
            }
        }

        return layerConfig;
    }

    public rootSiteLayerConfig = (
        recordType: LhlpRecordType,
        recordSubType: LhlpRecordSubType = null,
        opts: IVectorLayerGroupConfig = {}
    ): IVectorLayerGroupConfig | IWmsLayerGroupConfig =>
    {
        let layerConfig: IVectorLayerGroupConfig = null;

        if (recordSubType !== null) {

            switch (recordSubType) {

                case LhlpRecordSubType.AssetTypeBuilding:
                    layerConfig = this.AssetTypeBuildingAll(opts);
                    break;
    
                case LhlpRecordSubType.AssetTypeParkOrGarden:
                    layerConfig = this.AssetTypeParkOrGardenAll(opts);
                    break;
    
                case LhlpRecordSubType.AssetTypeLandmarkOrArtwork:
                    layerConfig = this.AssetTypeLandmarkOrArtworkAll(opts);
                    break;
    
                case LhlpRecordSubType.AssetTypeOtherSiteStructure:
                    layerConfig = this.AssetTypeOtherSiteStructureAll(opts);
                    break;
            }
        }

        if(layerConfig === null) {
            
            switch (recordType) {  
                case LhlpRecordType.LocalListArea:
                    layerConfig = this.LocalListAreaAll(opts);
                    break;

                case LhlpRecordType.LocalListSubAreas:
                    layerConfig = this.LocalListSubAreasAll(opts);
                    break;
            }
        }

        return layerConfig;
    }

    private Asset = (opts: IVectorLayerGroupConfig, subType: LhlpRecordSubType, assetId: number): IVectorLayerGroupConfig => {

        return {
            ...{
                displayName: "Selected Asset",
                ref: "SelectAssetRecord",
                type: LhlpRecordType.ASSET,
                subType: subType,
                strategy: VectorLayerStrategy.All,
                zoomToDataBounds: ZoomToDataBounds.Always,                
                url: this.mapServerProxyUrl,
                legend: { polygon: true, line: false },
                visibleOnLoad: true,
                queryable:false,
                wfsParams: this.paramSource3857(),
                useWfsBboxRequestParameter: false,
                restrictMapExtentToDataBounds: false,
                urlParams: {
                    assetId: assetId
                },                
                layers: [
                    {
                        layerName: "AssetPointsById",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "AssetPolygonsById",
                        minZoom: 1,
                        maxZoom: 19
                    }

                ]
            }, ...opts
        };
    }

    //this one currently won't work as map server is configured to want an asset type
    private Assets = (opts: IVectorLayerGroupConfig, isPublic: boolean = false): IVectorLayerGroupConfig => {
        let ispublic: string = isPublic?"Public":"";
        return {
            ...{
                displayName: "Assets",
                ref: "Assets" + ispublic,
                type: LhlpRecordType.ASSET,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapServerProxyUrl,
                urlParams: { 
                    assetTypeId: LhlpRecordSubType.NotSet,
                    isPublic: isPublic?1:0,
                    siteId: this.siteId,
                    siteFolder: this.siteFolder
                },
                legend: { polygon: false, line: false },
                visibleOnFirstLoad: false,
                wfsParams: this.paramSource3857(),
                useWfsBboxRequestParameter: false,
                layers: [
                    {
                        layerName: "AssetPoints",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "AssetPolygons",
                        minZoom: 1,
                        maxZoom: 19
                    }                    
                ]
            }, ...opts
        };
    }

    private MakeAssetLayerTitleWithHelp = (subType: LhlpRecordSubType): string => {        
        let displayName: string = "Undefined in database?";
        for(let typeStyle of this.mapConfig.TypeStyles) {
            if(typeStyle.TypeId == subType) {
                let layerName: string = typeStyle.LayerName;
                if(layerName) displayName = layerName; //override displayName if provided
                let layerHelpText: string = typeStyle.LayerHelpText;
                let layerHelpUrl: string = typeStyle.LayerHelpUrl;
                if(layerHelpText) {
                    let helpIcon: string = ' <i title="More information about this layer..." class="fas fa-question-circle LayerHelp" data-helptext="' +layerHelpText+'" data-helpurl="'+layerHelpUrl+'" onclick="return ShowMapLayerHelp(this)"></i>';
                    displayName += helpIcon;
                }
                break;
            }
        }       
        return "<span class=\"LayerName\">" + displayName + "</span>";
    }

    private AssetTypeBuilding = (opts: IVectorLayerGroupConfig, isPublic: boolean = false): IVectorLayerGroupConfig => {
        let ispublic: string = isPublic?"Public":"";        
        let displayName: string = this.MakeAssetLayerTitleWithHelp(LhlpRecordSubType.AssetTypeBuilding);
        return {
            ...{
                displayName: displayName,
                ref: "AssetTypeBuilding" + ispublic,
                type: LhlpRecordType.ASSET,
                subType: LhlpRecordSubType.AssetTypeBuilding,
                strategy: VectorLayerStrategy.All, //Bbox strategy stopped working for some reason...
                url: this.mapServerProxyUrl,
                urlParams: { 
                    assetTypeId: LhlpRecordSubType.AssetTypeBuilding,
                    isPublic: isPublic?1:0,
                    siteId: this.siteId,
                    siteFolder: this.siteFolder
                },
                legend: { polygon: true, line: false },
                visibleOnFirstLoad: true,
                wfsParams: this.paramSource3857(),
                useWfsBboxRequestParameter: false,
                layers: [
                    {
                        layerName: "AssetPolygons",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "AssetPoints",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    
                ]
            }, ...opts
        };
    }

    private AssetTypeParkOrGarden = (opts: IVectorLayerGroupConfig, isPublic: boolean = false): IVectorLayerGroupConfig => {
        let ispublic: string = isPublic?"Public":"";
        let displayName: string = this.MakeAssetLayerTitleWithHelp(LhlpRecordSubType.AssetTypeParkOrGarden);
        return {
            ...{
                displayName: displayName,
                ref: "AssetTypeParkOrGarden" + ispublic,
                type: LhlpRecordType.ASSET,
                subType: LhlpRecordSubType.AssetTypeParkOrGarden,
                strategy: VectorLayerStrategy.All,
                url: this.mapServerProxyUrl,
                urlParams: { 
                    assetTypeId: LhlpRecordSubType.AssetTypeParkOrGarden,
                    isPublic: isPublic?1:0,
                    siteId: this.siteId,
                    siteFolder: this.siteFolder
                },
                legend: { polygon: true, line: false },
                visibleOnFirstLoad: true,
                wfsParams: this.paramSource3857(),
                useWfsBboxRequestParameter: false,
                layers: [
                    {
                        layerName: "AssetPolygons",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "AssetPoints",
                        minZoom: 1,
                        maxZoom: 19
                    },                      
                ]
            }, ...opts
        };
    }

    private AssetTypeLandmarkOrArtwork = (opts: IVectorLayerGroupConfig, isPublic: boolean = false): IVectorLayerGroupConfig => {
        let ispublic: string = isPublic?"Public":"";
        let displayName: string = this.MakeAssetLayerTitleWithHelp(LhlpRecordSubType.AssetTypeLandmarkOrArtwork);
        return {
            ...{
                displayName: displayName,
                ref: "AssetTypeLandmarkOrArtwork" + ispublic,
                type: LhlpRecordType.ASSET,
                subType: LhlpRecordSubType.AssetTypeLandmarkOrArtwork,
                strategy: VectorLayerStrategy.All,
                url: this.mapServerProxyUrl,
                urlParams: { 
                    assetTypeId: LhlpRecordSubType.AssetTypeLandmarkOrArtwork,
                    isPublic: isPublic?1:0,
                    siteId: this.siteId,
                    siteFolder: this.siteFolder
                },
                legend: { polygon: true, line: false },
                visibleOnFirstLoad: true,
                wfsParams: this.paramSource3857(),
                useWfsBboxRequestParameter: false,
                layers: [
                    {
                        layerName: "AssetPolygons",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "AssetPoints",
                        minZoom: 1,
                        maxZoom: 19
                    },                     
                ]
            }, ...opts
        };
    }

    private AssetTypeOtherSiteStructure = (opts: IVectorLayerGroupConfig, isPublic: boolean = false): IVectorLayerGroupConfig => {
        let ispublic: string = isPublic?"Public":"";
        let displayName: string = this.MakeAssetLayerTitleWithHelp(LhlpRecordSubType.AssetTypeOtherSiteStructure);
       return {
            ...{
                displayName: displayName,
                ref: "AssetTypeOtherSiteStructure" + ispublic,
                type: LhlpRecordType.ASSET,
                subType: LhlpRecordSubType.AssetTypeOtherSiteStructure,
                strategy: VectorLayerStrategy.All,
                url: this.mapServerProxyUrl,
                urlParams: { 
                    assetTypeId: LhlpRecordSubType.AssetTypeOtherSiteStructure,
                    isPublic: isPublic?1:0,
                    siteId: this.siteId,
                    siteFolder: this.siteFolder
                },
                legend: { polygon: true, line: false },
                visibleOnFirstLoad: true,
                wfsParams: this.paramSource3857(),
                useWfsBboxRequestParameter: false,
                layers: [
                    {
                        layerName: "AssetPolygons",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "AssetPoints",
                        minZoom: 1,
                        maxZoom: 19
                    },                      
                ]
            }, ...opts
        };
    }

    private LocalListSubAreas = (opts: IWmsLayerGroupConfig): IWmsLayerGroupConfig => {
        return {
            ...{
                displayName: this.mapConfig.SubAreaLayerTitle,
                ref: "LocalListSubAreas",
                type: LhlpRecordType.LocalListSubAreas,
                url: this.mapServerProxyUrl,
                urlParams: { 
                    siteId: this.siteId,
                    siteFolder: this.siteFolder
                },
                visibleOnFirstLoad: false,
                info_format: "text/html",
                layers: [
                    {
                        layerName: "ProjectSubAreaPolygons",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private LocalListArea = (opts: IWmsLayerGroupConfig): IWmsLayerGroupConfig => {
        return {
            ...{
                displayName: "Local List Area",
                ref: "LocalListArea",
                type: LhlpRecordType.LocalListArea,
                url: this.mapServerProxyUrl,
                urlParams: { 
                    siteId: this.siteId,
                    siteFolder: this.siteFolder
                },
                visibleOnFirstLoad: true,
                info_format: "text/html",
                layers: [
                    {
                        layerName: "ProjectAreaPolygons",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }


    //******************************************************************************************************************************** */
    //ROOT SITE MAP LAYERS - providing an overview of all project areas and assets

    private AssetTypeBuildingAll = (opts: IVectorLayerGroupConfig): IVectorLayerGroupConfig => {  
        let displayName: string = this.MakeAssetLayerTitleWithHelp(LhlpRecordSubType.AssetTypeBuilding);
        return {
            ...{
                displayName: displayName,
                ref: "AssetTypeBuildingAll",
                type: LhlpRecordType.ASSET,
                subType: LhlpRecordSubType.AssetTypeBuilding,
                strategy: VectorLayerStrategy.All, //Bbox strategy stopped working for some reason...
                url: this.mapServerProxyUrl,
                urlParams: { 
                    assetTypeId: LhlpRecordSubType.AssetTypeBuilding
                },
                legend: { polygon: true, line: false },
                visibleOnFirstLoad: true,
                wfsParams: this.paramSource3857(),
                useWfsBboxRequestParameter: false,
                layers: [
                    {
                        layerName: "AllListedAssetPolygons",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "AllListedAssetPoints",
                        minZoom: 1,
                        maxZoom: 19
                    },                     
                ]
            }, ...opts
        };
    }

    private AssetTypeParkOrGardenAll = (opts: IVectorLayerGroupConfig): IVectorLayerGroupConfig => {
        let displayName: string = this.MakeAssetLayerTitleWithHelp(LhlpRecordSubType.AssetTypeParkOrGarden);
        return {
            ...{
                displayName: displayName,
                ref: "AssetTypeParkOrGardenAll",
                type: LhlpRecordType.ASSET,
                subType: LhlpRecordSubType.AssetTypeParkOrGarden,
                strategy: VectorLayerStrategy.All,
                url: this.mapServerProxyUrl,
                urlParams: { 
                    assetTypeId: LhlpRecordSubType.AssetTypeParkOrGarden
                },
                legend: { polygon: true, line: false },
                visibleOnFirstLoad: true,
                wfsParams: this.paramSource3857(),
                useWfsBboxRequestParameter: false,
                layers: [
                    {
                        layerName: "AllListedAssetPolygons",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "AllListedAssetPoints",
                        minZoom: 1,
                        maxZoom: 19
                    },                     
                ]
            }, ...opts
        };
    }

    private AssetTypeLandmarkOrArtworkAll = (opts: IVectorLayerGroupConfig): IVectorLayerGroupConfig => {
        let displayName: string = this.MakeAssetLayerTitleWithHelp(LhlpRecordSubType.AssetTypeLandmarkOrArtwork);
        return {
            ...{
                displayName: displayName,
                ref: "AssetTypeLandmarkOrArtworkAll",
                type: LhlpRecordType.ASSET,
                subType: LhlpRecordSubType.AssetTypeLandmarkOrArtwork,
                strategy: VectorLayerStrategy.All,
                url: this.mapServerProxyUrl,
                urlParams: { 
                    assetTypeId: LhlpRecordSubType.AssetTypeLandmarkOrArtwork
                },
                legend: { polygon: true, line: false },
                visibleOnFirstLoad: true,
                wfsParams: this.paramSource3857(),
                useWfsBboxRequestParameter: false,
                layers: [
                    {
                        layerName: "AllListedAssetPolygons",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "AllListedAssetPoints",
                        minZoom: 1,
                        maxZoom: 19
                    },                     
                ]
            }, ...opts
        };
    }

    private AssetTypeOtherSiteStructureAll = (opts: IVectorLayerGroupConfig): IVectorLayerGroupConfig => {
       let displayName: string = this.MakeAssetLayerTitleWithHelp(LhlpRecordSubType.AssetTypeOtherSiteStructure);
       return {
            ...{
                displayName: displayName,
                ref: "AssetTypeOtherSiteStructureAll",
                type: LhlpRecordType.ASSET,
                subType: LhlpRecordSubType.AssetTypeOtherSiteStructure,
                strategy: VectorLayerStrategy.All,
                url: this.mapServerProxyUrl,
                urlParams: { 
                    assetTypeId: LhlpRecordSubType.AssetTypeOtherSiteStructure,
                },
                legend: { polygon: true, line: false },
                visibleOnFirstLoad: true,
                wfsParams: this.paramSource3857(),
                useWfsBboxRequestParameter: false,
                layers: [
                    {
                        layerName: "AllListedAssetPolygons",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "AllListedAssetPoints",
                        minZoom: 1,
                        maxZoom: 19
                    },                     
                ]
            }, ...opts
        };
    }

    private LocalListSubAreasAll = (opts: IWmsLayerGroupConfig): IWmsLayerGroupConfig => {
        return {
            ...{
                displayName: this.mapConfig.SubAreaLayerTitle,
                ref: "LocalListSubAreasAll",
                type: LhlpRecordType.LocalListSubAreas,
                url: this.mapServerProxyUrl,
                urlParams: { 
                    siteId: this.siteId,
                    siteFolder: this.siteFolder
                },
                visibleOnFirstLoad: false,
                info_format: "text/html",
                layers: [
                    {
                        layerName: "SubAreaPolygons",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private LocalListAreaAll = (opts: IWmsLayerGroupConfig): IWmsLayerGroupConfig => {
        return {
            ...{
                displayName: "Local List Area",
                ref: "LocalListAreaAll",
                type: LhlpRecordType.LocalListArea,
                url: this.mapServerProxyUrl,
                urlParams: { 
                    siteId: this.siteId,
                    siteFolder: this.siteFolder
                },
                visibleOnFirstLoad: true,
                info_format: "text/html",
                layers: [
                    {
                        layerName: "AreaPolygons",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }



    //******************************************************************************************************************************** */
    //NHLE Layers
    private MakeNHLELayerTitleWithHelp = (layerName: string, layerHelpUrl: string): string => {        
        let displayName: string =  layerName;
        let layerHelpText: string = layerName + " are legally protected through their inclusion on the National Heritage List for England (NHLE)";
        let helpIcon: string = ' <i title="More information about this layer..." class="fas fa-question-circle LayerHelp" data-helptext="' +layerHelpText+'" data-helpurl="'+layerHelpUrl+'" onclick="return ShowMapLayerHelp(this)"></i>';
        displayName += helpIcon;
        return "<span class=\"LayerName\">" + displayName + "</span>";
    }

    private ListedBuilding = (opts: IWmsLayerGroupConfig): IWmsLayerGroupConfig => {
        return {
            ...{
                displayName: this.MakeNHLELayerTitleWithHelp(
                    "Listed Buildings", 
                    "https://historicengland.org.uk/listing/what-is-designation/listed-buildings/"
                ),
                ref: "ListedBuildings",
                type: LhlpRecordType.NHLE,
                subType: LhlpRecordSubType.ListedBuilding,
                url: this.ogcProxyUrl,
                visibleOnFirstLoad: false,
                info_format: "text/html",
                layers: [
                    {
                        layerName: "ListedBuildings",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private ScheduledMonument = (opts: IWmsLayerGroupConfig): IWmsLayerGroupConfig => {
        return {
            ...{
                displayName: this.MakeNHLELayerTitleWithHelp(
                    "Scheduled Monuments", 
                    "https://historicengland.org.uk/listing/what-is-designation/scheduled-monuments/"
                ),
                ref: "ScheduledMonuments",
                type: LhlpRecordType.NHLE,
                subType: LhlpRecordSubType.ScheduledMonument,
                url: this.ogcProxyUrl,
                visibleOnFirstLoad: false,
                info_format: "text/html",
                layers: [
                    {
                        layerName: "ScheduledMonuments",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }
  
    private RegisteredParkAndGarden = (opts: IWmsLayerGroupConfig): IWmsLayerGroupConfig => {
        return {
            ...{
                displayName: this.MakeNHLELayerTitleWithHelp(
                    "Parks and Gardens", 
                    "https://historicengland.org.uk/listing/what-is-designation/registered-parks-and-gardens/"
                ),
                ref: "ParksandGardens",
                type: LhlpRecordType.NHLE,
                subType: LhlpRecordSubType.RegisteredParkAndGarden,
                url: this.ogcProxyUrl,
                visibleOnFirstLoad: false,
                info_format: "text/html",
                layers: [
                    {
                        layerName: "ParksandGardens",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private Battlefield = (opts: IWmsLayerGroupConfig): IWmsLayerGroupConfig => {
        return {
            ...{
                displayName: this.MakeNHLELayerTitleWithHelp(
                    "Battlefields",
                    "https://historicengland.org.uk/listing/what-is-designation/registered-battlefields/"
                ),
                ref: "Battlefields",
                type: LhlpRecordType.NHLE,
                subType: LhlpRecordSubType.Battlefield,
                url: this.ogcProxyUrl,
                visibleOnFirstLoad: false,
                info_format: "text/html",
                layers: [
                    {
                        layerName: "Battlefields",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private ProtectedWreck = (opts: IWmsLayerGroupConfig): IWmsLayerGroupConfig => {
        return {
            ...{
                displayName: this.MakeNHLELayerTitleWithHelp(
                    "Protected Wrecks",
                    "https://historicengland.org.uk/listing/what-is-designation/protected-wreck-sites/"
                ),
                ref: "ProtectedWrecks",
                type: LhlpRecordType.NHLE,
                subType: LhlpRecordSubType.ProtectedWreck,
                url: this.ogcProxyUrl,
                visibleOnFirstLoad: false,
                info_format: "text/html",
                layers: [
                    {
                        layerName: "ProtectedWrecks",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private MakeWorldHeritageLayerTitleWithHelp = (layerName: string, layerHelpUrl: string): string => {        
        let displayName: string =  layerName;
        let layerHelpText: string = "World Heritage Sites are legally protected through their inclusion on the World Heritage List.";
        let helpIcon: string = ' <i title="More information about this layer..." class="fas fa-question-circle LayerHelp" data-helptext="' +layerHelpText+'" data-helpurl="'+layerHelpUrl+'" onclick="return ShowMapLayerHelp(this)"></i>';
        displayName += helpIcon;
        return "<span class=\"LayerName\">" + displayName + "</span>";
    }

    private WorldHeritageSite = (opts: IWmsLayerGroupConfig): IWmsLayerGroupConfig => {
        return {
            ...{
                displayName: this.MakeWorldHeritageLayerTitleWithHelp(
                    "World Heritage Sites",
                    "https://whc.unesco.org/"
                ),
                ref: "WorldHeritageSites",
                type: LhlpRecordType.NHLE,
                subType: LhlpRecordSubType.WorldHeritageSite,
                url: this.ogcProxyUrl,
                visibleOnFirstLoad: false,
                info_format: "text/html",
                layers: [
                    {
                        layerName: "WorldHeritageSites",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }
}

